// contexts/CartContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { API_URL } from '../constants'
import { languageUtil } from './languageUtils'

const CartContext = createContext()

export const CartProvider = ({ children }) => {
    const { t } = useTranslation()
    const { language } = languageUtil()
    const [cart, setCart] = useState({
        items: [],
        total: 0,
        currency: 'USD',
        cartToken: null,
        isLoading: false,
        loadingMessage: null,
        error: null,
    })

    // Check for existing valid token
    const checkExistingToken = useCallback(() => {
        const storedToken = localStorage.getItem('cartToken')
        const expiresAt = localStorage.getItem('cartTokenExpiresAt')

        if (storedToken && expiresAt && Date.now() < parseInt(expiresAt)) {
            return storedToken
        }
        return null
    }, [])

    // Create new cart token
    const createNewCartToken = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/cart`, {
                method: 'POST',
                headers: { 'Accept-Language': language },
            })

            if (!response.ok) throw new Error(t('cart.error.fetch_cart'))

            const data = await response.json()
            const newExpiresAt = Date.now() + 30 * 60 * 1000 // 30 minutes

            localStorage.setItem('cartToken', data.token)
            localStorage.setItem('cartTokenExpiresAt', newExpiresAt.toString())

            return data.token
        } catch (error) {
            console.error('Error creating cart:', error)
            throw error
        }
    }, [t])

    // Get or create cart token
    const getValidCartToken = useCallback(async () => {
        const existingToken = checkExistingToken()
        if (existingToken) return existingToken

        return await createNewCartToken()
    }, [checkExistingToken, createNewCartToken])

    // Fetch cart data
    const fetchCartData = useCallback(
        async (token, silent = false) => {
            try {
                setCart(prev => ({
                    ...prev,
                    isLoading: !silent,
                    loadingMessage: !silent ? t('cart.loading.refreshing') : null,
                }))

                const response = await fetch(`${API_URL}/cart/${token}`, {
                    headers: { 'Accept-Language': language },
                })

                if (!response.ok) throw new Error(t('cart.error.fetch_cart'))

                const data = await response.json()

                setCart({
                    items: data.data.items || [],
                    total: data.data.total || 0,
                    currency: data.data.currency || 'USD',
                    cartToken: token,
                    isLoading: false,
                    loadingMessage: null,
                    error: null,
                })
            } catch (error) {
                console.error('Error fetching cart:', error)
                setCart(prev => ({
                    ...prev,
                    isLoading: false,
                    loadingMessage: null,
                    error: error.message,
                }))
                throw error
            }
        },
        [t]
    )

    // Initialize cart
    useEffect(() => {
        const initializeCart = async () => {
            try {
                setCart(prev => ({
                    ...prev,
                    isLoading: true,
                    loadingMessage: t('cart.loading.initializing'),
                }))

                const token = await getValidCartToken()
                await fetchCartData(token)
            } catch (error) {
                console.error('Initial cart load failed:', error)
                setCart(prev => ({
                    ...prev,
                    isLoading: false,
                    loadingMessage: null,
                    error: error.message,
                }))
            }
        }

        initializeCart()
    }, [getValidCartToken, fetchCartData, t])

    // Add donation item to cart
    const addDonationItem = useCallback(
        async ({ data, id = null, type = 'project', onSuccess = () => {}, onError = () => {} }) => {
            try {
                setCart(prev => ({
                    ...prev,
                    isLoading: true,
                    loadingMessage: t('cart.loading.adding_item'),
                    error: null,
                }))

                const token = await getValidCartToken()

                const body = {
                    donation_type: data.donation_type || type || 'project',
                    ...(data.id !== null && data.id
                        ? { donation_option_id: data.id }
                        : { parent_id: id }),
                    quantity: data.quantity || 1,
                    ...(data.amount ? { amount: data.amount } : {}),
                    ...(data.frequent_option ? { frequent_option: data.frequent_option } : {}),
                }

                const response = await fetch(`${API_URL}/cart/${token}/items`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': language,
                    },
                    body: JSON.stringify(body),
                })

                if (!response.ok) {
                    const text = await response.text()
                    throw new Error(t('cart.error.add_item'))
                }

                const result = await response.json()
                await fetchCartData(token, true) // Silent refresh

                onSuccess(result)
                return result
            } catch (error) {
                console.error('Error adding donation:', error)
                setCart(prev => ({
                    ...prev,
                    error: error.message,
                    isLoading: false,
                    loadingMessage: null,
                }))
                onError(error)
                throw error
            }
        },
        [getValidCartToken, fetchCartData, t]
    )

    // Delete item from cart
    const deleteItem = useCallback(
        async itemId => {
            try {
                setCart(prev => ({
                    ...prev,
                    isLoading: true,
                    loadingMessage: t('cart.loading.removing_item'),
                    error: null,
                }))

                const token = await getValidCartToken()
                const response = await fetch(`${API_URL}/cart/${token}/items/${itemId}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': language,
                    },
                })

                if (!response.ok) throw new Error(t('cart.error.remove_item'))

                // Optimistic update
                setCart(prev => ({
                    ...prev,
                    items: prev.items.filter(item => item.item_id !== itemId),
                    isLoading: false,
                    loadingMessage: null,
                    error: null,
                }))

                await fetchCartData(token, true) // Silent refresh
            } catch (error) {
                console.error('Error deleting item:', error)
                setCart(prev => ({
                    ...prev,
                    error: error.message,
                    isLoading: false,
                    loadingMessage: null,
                }))
                throw error
            }
        },
        [getValidCartToken, fetchCartData, t]
    )

    // Update cart item
    const updateItem = useCallback(
        async (itemId, updateData, onSuccess = () => {}, onError = () => {}) => {
            try {
                setCart(prev => ({
                    ...prev,
                    isLoading: true,
                    loadingMessage: t('cart.loading.updating_item'),
                    error: null,
                }))

                const token = await getValidCartToken()

                // Find the item to get its details
                const item = cart.items.find(item => item.id === itemId || item.item_id === itemId)
                if (!item) {
                    throw new Error(t('cart.error.item_not_found'))
                }

                // Prepare update payload according to your API specification
                const body = {
                    donation_type: item.donation_type,
                    parent_id: item.parent_id || item.parent_class?.id,
                    quantity: updateData.quantity || item.quantity,
                    amount: item.amount,
                    frequent_option: updateData.frequent_option || item.frequent_option || 'single',
                }

                const response = await fetch(`${API_URL}/cart/${token}/items/${itemId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': language,
                    },
                    body: JSON.stringify(body),
                })

                if (!response.ok) {
                    const text = await response.text()
                    throw new Error(t('cart.error.update_item'))
                }

                const result = await response.json()

                // Optimistic update
                setCart(prev => ({
                    ...prev,
                    items: prev.items.map(item =>
                        item.id === itemId || item.item_id === itemId
                            ? {
                                  ...item,
                                  quantity: updateData.quantity || item.quantity,
                                  frequent_option:
                                      updateData.frequent_option || item.frequent_option,
                                  // Update subtotal based on new quantity
                                  subtotal:
                                      (updateData.quantity || item.quantity) * item.original_amount,
                              }
                            : item
                    ),
                    isLoading: false,
                    loadingMessage: null,
                    error: null,
                }))

                // Silent refresh to ensure data is in sync
                await fetchCartData(token, true)

                onSuccess(result)
                return result
            } catch (error) {
                console.error('Error updating item:', error)
                setCart(prev => ({
                    ...prev,
                    error: error.message,
                    isLoading: false,
                    loadingMessage: null,
                }))
                onError(error)
                throw error
            }
        },
        [getValidCartToken, fetchCartData, cart.items, t]
    )

    // Update currency
    const updateCurrency = useCallback(
        async currency => {
            try {
                setCart(prev => ({
                    ...prev,
                    isLoading: true,
                    loadingMessage: t('cart.loading.updating_currency'),
                    error: null,
                }))

                const token = await getValidCartToken()
                const response = await fetch(`${API_URL}/cart/${token}/currency`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': language,
                    },
                    body: JSON.stringify({ currency }),
                })

                if (!response.ok) throw new Error(t('cart.error.update_currency'))

                const data = await response.json()
                setCart({
                    items: data.items || [],
                    total: data.total || 0,
                    currency: data.currency || 'USD',
                    cartToken: token,
                    isLoading: false,
                    loadingMessage: null,
                    error: null,
                })
            } catch (error) {
                console.error('Error updating currency:', error)
                setCart(prev => ({
                    ...prev,
                    error: error.message,
                    isLoading: false,
                    loadingMessage: null,
                }))
                throw error
            }
        },
        [getValidCartToken, t]
    )

    return (
        <CartContext.Provider
            value={{
                cart,
                addDonationItem,
                deleteItem,
                updateItem,
                fetchCartData,
                getValidCartToken,
                checkExistingToken,
                createNewCartToken,
                updateCurrency,
            }}
        >
            {children}
        </CartContext.Provider>
    )
}

export const useCart = () => {
    const context = useContext(CartContext)
    if (!context) {
        throw new Error('useCart must be used within a CartProvider')
    }
    return context
}
